// ============================================================================
// Hazel and Fawn — AI response engine
// Detailed, useful responses that connect directly to what the user says.
// Fully local & deterministic so it always works, no external keys needed.
// ============================================================================

export type Topic =
  | 'school'
  | 'productivity'
  | 'focus'
  | 'stress'
  | 'sadness'
  | 'anxiety'
  | 'decision'
  | 'relationships'
  | 'sleep'
  | 'money'
  | 'health'
  | 'motivation'
  | 'learning'
  | 'gratitude'
  | 'faith'
  | 'goals'
  | 'confusion'
  | 'general'

const TOPIC_KEYWORDS: Record<Exclude<Topic, 'general'>, string[]> = {
  school: ['school', 'class', 'exam', 'test', 'homework', 'assignment', 'study', 'grade', 'teacher', 'college', 'university', 'lecture', 'quiz', 'essay', 'gpa', 'semester', 'professor'],
  productivity: ['productive', 'productivity', 'procrastinate', 'procrastination', 'todo', 'to-do', 'task', 'deadline', 'busy', 'overwhelmed', 'time management', 'organize', 'schedule', 'workflow', 'efficient'],
  focus: ['focus', 'concentrate', 'distracted', 'distraction', 'attention', 'zone', 'deep work'],
  stress: ['stress', 'stressed', 'pressure', 'burnout', 'burned out', 'exhausted', 'tired', 'too much', 'can\'t cope', 'overload'],
  sadness: ['sad', 'down', 'depressed', 'lonely', 'cry', 'crying', 'hurt', 'heartbroken', 'grief', 'grieving', 'empty', 'hopeless'],
  anxiety: ['anxious', 'anxiety', 'worried', 'worry', 'nervous', 'panic', 'afraid', 'scared', 'fear', 'overthinking', 'racing thoughts'],
  decision: ['decision', 'decide', 'choose', 'choice', 'should i', 'option', 'options', 'whether', 'pros and cons', 'stuck between', 'torn'],
  relationships: ['friend', 'friends', 'family', 'partner', 'boyfriend', 'girlfriend', 'relationship', 'argument', 'fight', 'lonely', 'people', 'social', 'roommate', 'parent', 'mom', 'dad'],
  sleep: ['sleep', 'insomnia', 'awake', 'bed', 'rest', 'nap', 'nighttime', 'night', 'dream', 'restless', 'wind down', 'tired'],
  money: ['money', 'budget', 'save', 'saving', 'spend', 'spending', 'debt', 'income', 'afford', 'finance', 'financial', 'earn'],
  health: ['health', 'exercise', 'workout', 'eat', 'diet', 'water', 'body', 'sick', 'headache', 'energy', 'wellness', 'habit'],
  motivation: ['motivation', 'motivated', 'lazy', 'give up', 'giving up', 'unmotivated', 'inspire', 'inspired', 'drive', 'discipline', 'push through'],
  learning: ['learn', 'learning', 'understand', 'explain', 'how does', 'what is', 'why does', 'concept', 'meaning', 'define', 'research', 'curious', 'teach me'],
  gratitude: ['grateful', 'gratitude', 'thankful', 'blessed', 'appreciate', 'appreciation'],
  faith: ['god', 'jesus', 'faith', 'pray', 'prayer', 'bible', 'scripture', 'lord', 'spiritual', 'church', 'blessing', 'soul', 'holy'],
  goals: ['goal', 'goals', 'dream', 'future', 'plan', 'ambition', 'achieve', 'accomplish', 'vision', 'purpose', 'career'],
  confusion: ['confused', 'confusing', 'lost', 'don\'t know', 'dont know', 'unsure', 'uncertain', 'no idea', 'help me understand'],
}

export function detectTopics(input: string): Topic[] {
  const text = ' ' + input.toLowerCase() + ' '
  const found: Topic[] = []
  ;(Object.keys(TOPIC_KEYWORDS) as Exclude<Topic, 'general'>[]).forEach((topic) => {
    if (TOPIC_KEYWORDS[topic].some((kw) => text.includes(kw))) found.push(topic)
  })
  if (found.length === 0) found.push('general')
  return found
}

export function isQuestion(input: string): boolean {
  const t = input.trim().toLowerCase()
  return (
    t.endsWith('?') ||
    /^(what|why|how|when|where|who|which|should|can|could|would|is|are|do|does|did|will)\b/.test(t)
  )
}

// Pull a short, human-readable summary of what the user actually said.
export function echoBack(input: string): string {
  const clean = input.trim().replace(/\s+/g, ' ')
  if (clean.length <= 140) return clean
  return clean.slice(0, 137).trimEnd() + '…'
}

function keyPhrase(input: string): string {
  const clean = input.trim().replace(/[.?!]+$/, '')
  const words = clean.split(/\s+/)
  if (words.length <= 8) return clean.toLowerCase()
  return words.slice(0, 8).join(' ').toLowerCase() + '…'
}

// ============================================================================
// Topic-specific guidance blocks (rich, practical, reusable)
// ============================================================================
function guidanceFor(topic: Topic, input: string): string {
  const phrase = keyPhrase(input)
  switch (topic) {
    case 'school':
      return `Since this touches on **school**, here's a way to make it lighter. Break the work into the smallest honest next step — not "study biology," but "read page 1 and underline three ideas." Set a gentle 25-minute timer, work only on that one page, then rest for 5. Your brain remembers what it *retrieves*, so after reading, close the book and try to say it back in your own words. Teach it to an imaginary friend (or a very patient stuffed deer 🦌). If a subject feels impossible, it usually means the *previous* building block is shaky — go back one step and the current one gets easier.`
    case 'productivity':
      return `On **getting things done**: the feeling of "too much" almost always comes from carrying every task in your head at once. Empty your mind onto paper — write down every open loop. Then circle the *one* that would make the rest feel lighter, and do only that. Momentum is created, not waited for. Protect a single 45-minute block, silence notifications, and let "done imperfectly" beat "perfect but never started."`
    case 'focus':
      return `For **focus**: distraction is usually a sign the task is either unclear or a little scary. Name the exact next action out loud, remove one temptation (phone in another room, one tab open), and start a short timer. Focus is a muscle — the first few minutes feel awkward, then attention settles. When your mind wanders, don't scold it; just gently guide it back, the way you'd guide a fawn back to the path.`
    case 'stress':
      return `I hear that things feel **heavy** right now. Stress is your mind caring loudly about something that matters. Let's shrink it: place both feet on the floor, breathe in for four counts, hold for four, out for six. Then ask — *what is actually mine to carry today, and what can wait?* You don't have to solve the whole mountain, only the next small step of the path in front of you.`
    case 'anxiety':
      return `Worry tends to sprint ahead into futures that haven't happened. Gently bring yourself back to *now*: name five things you can see, four you can hear, three you can touch. Then separate the worry into "things I can influence" and "things I can't." Pour your energy only into the first list. You are safe in this moment, and this moment is the only one you actually have to live.`
    case 'sadness':
      return `I'm really glad you said this out loud — it takes courage. Sadness isn't a flaw; it's love and longing with nowhere to go for a moment. You don't have to fix the feeling right now. Let it be here, be gentle with yourself, drink some water, and reach toward one small comfort. Feelings move like weather — heavy clouds, then a clearing. You won't feel exactly this way forever.`
    case 'decision':
      return `Let's make this **decision** clearer. First, name what you're really choosing between in one sentence. For each option, ask three questions: *Which one honours who I want to become? Which one will I respect in a year? Which fear is quietly driving me?* Often the "right" choice isn't the safest — it's the one that lets you grow without betraying your values. Sleep on the top option; if you wake up relieved, that's your answer.`
    case 'relationships':
      return `With **people**, most tension is really an unmet need trying to be seen. Before reacting, name your own need clearly (to feel heard? respected? included?). When you speak, describe your feeling and the situation, not the other person's character — "I felt hurt when…" opens doors, "you always…" closes them. And remember: you can be kind and still have boundaries. Both can be true.`
    case 'sleep':
      return `For **rest**: your body follows signals of safety and darkness. Dim the lights an hour before bed, let your screen rest, and let your thoughts spill onto paper so your mind knows they're safely held for tomorrow. Slow your breathing until your exhale is longer than your inhale. Sleep can't be forced, only welcomed — so loosen your grip and let it come to you softly.`
    case 'money':
      return `On **money**: clarity beats worry. Look honestly at what comes in and what goes out, then give every dollar a gentle job — some for needs, a little for joy, a little set aside for future-you. Small consistent saving beats dramatic one-time effort. Money is simply a tool that gives your values room to breathe; let it serve your peace, not steal it.`
    case 'health':
      return `For your **body and energy**: you don't need a perfect routine, just a few kind anchors. Water first thing, some movement you actually enjoy, real food, and daylight on your face. Tiny habits repeated gently outlast grand plans done harshly. Treat your body like someone you're caring for, not a project to fix.`
    case 'motivation':
      return `Motivation is a lovely visitor but a terrible foreman — it comes *after* you start, not before. So make starting absurdly small: two minutes, one sentence, one push-up. Action creates the feeling you were waiting for. And connect the task to *why it matters to you* — not the pressure of should, but the pull of the person you're becoming.`
    case 'learning':
      return `Since you want to **understand** this, here's how real understanding forms: start from what you already know and build one honest link at a time. Ask "what is this *like*?" to find a familiar anchor, then "where does the comparison break?" to find the truth. Explain it back in plain words — if you can't, you've found the exact gap to fill next. Curiosity is the engine; questions are the fuel.`
    case 'gratitude':
      return `What a beautiful thing to notice. Gratitude literally rewires attention toward what's good and already here. Try naming *why* each thing matters, not just *what* it is — that's where the warmth deepens. The more specific the thanks, the more real the joy.`
    case 'faith':
      return `Since this touches something **deeper**, know that stillness itself can be a kind of prayer. You don't need perfect words — honesty is enough. Bring exactly what you feel, unpolished, and trust that it's received. Peace often arrives not by getting every answer, but by feeling less alone inside the question.`
    case 'goals':
      return `Let's give this **goal** a spine. Turn the dream into a direction, the direction into a milestone, and the milestone into this week's one small action. Vague goals scatter energy; a single concrete next step focuses it. Progress isn't a straight line — it's small, repeated returns to the path.`
    case 'confusion':
      return `Feeling **lost** is often the moment right before clarity — it means you're honest enough to admit you don't have it all figured out yet. Start by writing down what you *do* know for certain, even if it's small. Confusion shrinks the moment you name its edges. One clear question is worth more than ten anxious guesses.`
    default:
      return `Let's stay with what you shared about "${phrase}". Tell me a little more about the part that matters most to you — what would "better" actually look like here? The clearer the picture, the more useful I can be.`
  }
}

// ============================================================================
// PROFESSOR TED 🧸 — wise, warm, detailed mentor on life & learning
// ============================================================================
export function professorTed(input: string): string {
  const topics = detectTopics(input)
  const primary = topics[0]
  const secondary = topics.find((t) => t !== primary && t !== 'general')
  const opener = isQuestion(input)
    ? `That's a thoughtful question — "${echoBack(input)}". Let's think it through together.`
    : `Thank you for sharing this with me: "${echoBack(input)}". I've read it carefully, and here's what I'm noticing.`

  const blocks = [opener, guidanceFor(primary, input)]
  if (secondary) blocks.push(guidanceFor(secondary, input))

  blocks.push(
    `If I could leave you with one small assignment 🧸: choose the *single* smallest step you could take in the next hour, and let that be enough for today. You don't have to carry the whole thing at once. Come back and tell me how it goes — I'm genuinely on your side.`
  )
  return blocks.join('\n\n')
}

// ============================================================================
// LILY THE BEE 🐝 — Research Helper, clear & factual explanations
// ============================================================================
export function lilyTheBee(input: string): string {
  const phrase = keyPhrase(input)
  const q = input.trim()
  const opener = `🐝 Buzzing in on your question — "${echoBack(input)}". Let me gather the nectar and explain it clearly.`

  const structure = `Here's how I'd break "${phrase}" down so it actually clicks:

**1. The core idea, in one sentence.** Strip away the jargon and state the simplest true version of what you're asking about. Understanding almost always begins with a plain-language definition you could tell a curious 12-year-old.

**2. Why it works this way.** Nearly everything has a cause behind it — a mechanism, an incentive, or a rule. Ask "what's driving this?" A fact you can *explain* sticks far better than a fact you merely memorised.

**3. A familiar comparison.** Find something you already understand that behaves similarly, then notice where the comparison holds and where it breaks. That gap is usually where the real insight lives.

**4. A concrete example.** Abstract ideas feel slippery; a single specific example gives them a handle to hold onto.`

  const method = `To research "${phrase}" reliably: start broad to get the shape of the topic, then go narrow to a trustworthy primary source. Cross-check any surprising claim against at least two independent sources, notice *who* is saying it and *why*, and separate established facts from opinions and predictions. When you can restate the answer in your own words without peeking, you truly know it. 🍯`

  const invite = q.length < 25
    ? `Give me a bit more detail about exactly what you're trying to understand, and I'll tailor the explanation precisely to your question.`
    : `If you tell me which part is still fuzzy, I'll zoom right in on that piece and make it crystal clear.`

  return [opener, structure, method, invite].join('\n\n')
}

// ============================================================================
// SLEEP 🌙 — Willow the Fawn, gentle nighttime companion
// ============================================================================
export function willowTheFawn(input: string): string {
  const topics = detectTopics(input)
  const soothing = [
    `🌙 Hi, I'm Willow, your little fawn friend for the night. I heard you: "${echoBack(input)}". Let's set it down gently, together.`,
  ]

  if (topics.includes('anxiety') || topics.includes('stress') || topics.includes('confusion')) {
    soothing.push(`Your mind is still running through the meadow, and that's okay. We don't have to solve anything now — nighttime isn't for fixing, only for softening. Whatever is spinning, imagine placing it in a small basket by the door. It will still be there in the morning, and morning-you is wiser and more rested.`)
  } else if (topics.includes('sadness') || topics.includes('relationships')) {
    soothing.push(`It's tender, what you're carrying. Let it be here without needing to explain itself. You were brave today just by getting through it. Let your shoulders drop away from your ears, and let the day be finished now. You did enough. You *are* enough.`)
  } else if (topics.includes('school') || topics.includes('productivity') || topics.includes('goals')) {
    soothing.push(`The work will wait for you — it always does, and it's always more possible after rest. Your brain quietly files away everything you learned today while you sleep, so resting *is* part of the work. Let tomorrow's to-do list stay asleep too, just for now.`)
  } else {
    soothing.push(`Let's let the day drift away like a leaf on slow water. There's nothing left to do tonight but rest. You are safe, you are warm, and this quiet moment belongs entirely to you.`)
  }

  soothing.push(`Let's breathe like the forest does. Breathe in slowly through your nose… two… three… four. Now let it go, longer and softer… two… three… four… five… six. Feel your body grow heavier and warmer with each breath, sinking gently into the earth. 🍃`)

  soothing.push(`Now, a tiny drifting story: picture a meadow under a wide, silver moon. The grass is cool and the air smells of clover. A small fawn curls beside you, breathing slow and steady. The stars blink softly, one by one, like little lights being turned down for the night. Nothing is needed of you here. You can finally, gently, let go. 🌙✨`)

  soothing.push(`Sleep well, dear one. I'll be right here in the meadow if you need me. Goodnight. 🦌💤`)
  return soothing.join('\n\n')
}

// ============================================================================
// REFLECTION SPACE 🌷 — thoughtful reply + a relevant Bible verse
// ============================================================================
export interface Verse {
  ref: string
  text: string
}

const VERSES: Record<string, Verse[]> = {
  anxiety: [
    { ref: 'Philippians 4:6-7', text: 'Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God. And the peace of God, which transcends all understanding, will guard your hearts and your minds in Christ Jesus.' },
    { ref: '1 Peter 5:7', text: 'Cast all your anxiety on him because he cares for you.' },
    { ref: 'Matthew 6:34', text: 'Therefore do not worry about tomorrow, for tomorrow will worry about itself. Each day has enough trouble of its own.' },
  ],
  sadness: [
    { ref: 'Psalm 34:18', text: 'The Lord is close to the brokenhearted and saves those who are crushed in spirit.' },
    { ref: 'Psalm 147:3', text: 'He heals the brokenhearted and binds up their wounds.' },
    { ref: 'Revelation 21:4', text: 'He will wipe every tear from their eyes. There will be no more death or mourning or crying or pain.' },
  ],
  stress: [
    { ref: 'Matthew 11:28', text: 'Come to me, all you who are weary and burdened, and I will give you rest.' },
    { ref: 'Psalm 55:22', text: 'Cast your cares on the Lord and he will sustain you; he will never let the righteous be shaken.' },
    { ref: 'Isaiah 41:10', text: 'So do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you.' },
  ],
  fear: [
    { ref: '2 Timothy 1:7', text: 'For God has not given us a spirit of fear, but of power and of love and of a sound mind.' },
    { ref: 'Joshua 1:9', text: 'Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you wherever you go.' },
    { ref: 'Psalm 23:4', text: 'Even though I walk through the darkest valley, I will fear no evil, for you are with me.' },
  ],
  decision: [
    { ref: 'Proverbs 3:5-6', text: 'Trust in the Lord with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight.' },
    { ref: 'James 1:5', text: 'If any of you lacks wisdom, you should ask God, who gives generously to all without finding fault, and it will be given to you.' },
    { ref: 'Psalm 32:8', text: 'I will instruct you and teach you in the way you should go; I will counsel you with my loving eye on you.' },
  ],
  gratitude: [
    { ref: '1 Thessalonians 5:18', text: 'Give thanks in all circumstances; for this is God\'s will for you in Christ Jesus.' },
    { ref: 'Psalm 100:4', text: 'Enter his gates with thanksgiving and his courts with praise; give thanks to him and praise his name.' },
    { ref: 'James 1:17', text: 'Every good and perfect gift is from above, coming down from the Father of the heavenly lights.' },
  ],
  hope: [
    { ref: 'Jeremiah 29:11', text: 'For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.' },
    { ref: 'Romans 15:13', text: 'May the God of hope fill you with all joy and peace as you trust in him, so that you may overflow with hope by the power of the Holy Spirit.' },
    { ref: 'Lamentations 3:22-23', text: 'Because of the Lord\'s great love we are not consumed, for his compassions never fail. They are new every morning; great is your faithfulness.' },
  ],
  strength: [
    { ref: 'Philippians 4:13', text: 'I can do all this through him who gives me strength.' },
    { ref: 'Isaiah 40:31', text: 'But those who hope in the Lord will renew their strength. They will soar on wings like eagles; they will run and not grow weary.' },
    { ref: 'Psalm 46:1', text: 'God is our refuge and strength, an ever-present help in trouble.' },
  ],
  relationships: [
    { ref: 'Colossians 3:13', text: 'Bear with each other and forgive one another if any of you has a grievance against someone. Forgive as the Lord forgave you.' },
    { ref: '1 Corinthians 13:4-5', text: 'Love is patient, love is kind. It does not envy, it does not boast, it is not proud. It is not self-seeking, it is not easily angered, it keeps no record of wrongs.' },
    { ref: 'Ephesians 4:32', text: 'Be kind and compassionate to one another, forgiving each other, just as in Christ God forgave you.' },
  ],
  rest: [
    { ref: 'Psalm 23:1-3', text: 'The Lord is my shepherd, I lack nothing. He makes me lie down in green pastures, he leads me beside quiet waters, he refreshes my soul.' },
    { ref: 'Exodus 33:14', text: 'My Presence will go with you, and I will give you rest.' },
    { ref: 'Matthew 11:29', text: 'Take my yoke upon you and learn from me, for I am gentle and humble in heart, and you will find rest for your souls.' },
  ],
  general: [
    { ref: 'Psalm 46:10', text: 'Be still, and know that I am God.' },
    { ref: 'Zephaniah 3:17', text: 'The Lord your God is with you, the Mighty Warrior who saves. He will take great delight in you; in his love he will no longer rebuke you, but will rejoice over you with singing.' },
    { ref: 'Psalm 118:24', text: 'This is the day that the Lord has made; let us rejoice and be glad in it.' },
  ],
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

export function verseForInput(input: string): Verse {
  const t = ' ' + input.toLowerCase() + ' '
  const map: [string, string[]][] = [
    ['anxiety', ['anxious', 'anxiety', 'worried', 'worry', 'nervous', 'panic', 'overthink']],
    ['fear', ['afraid', 'scared', 'fear', 'terrified', 'dread']],
    ['sadness', ['sad', 'down', 'depressed', 'lonely', 'cry', 'grief', 'heartbroken', 'hopeless', 'empty', 'hurt']],
    ['stress', ['stress', 'overwhelmed', 'burnout', 'exhausted', 'pressure', 'too much', 'tired', 'weary']],
    ['decision', ['decision', 'decide', 'choose', 'choice', 'unsure', 'confused', 'lost', 'direction', 'wisdom']],
    ['gratitude', ['grateful', 'thankful', 'blessed', 'gratitude', 'appreciate', 'thank']],
    ['relationships', ['friend', 'family', 'forgive', 'argument', 'fight', 'relationship', 'partner', 'angry', 'anger']],
    ['rest', ['rest', 'sleep', 'peace', 'calm', 'quiet', 'still']],
    ['strength', ['weak', 'strength', 'strong', 'give up', 'can\'t', 'cannot', 'struggle', 'hard']],
    ['hope', ['hope', 'future', 'dream', 'goal', 'better', 'tomorrow']],
  ]
  for (const [key, kws] of map) {
    if (kws.some((k) => t.includes(k))) return pick(VERSES[key])
  }
  return pick(VERSES.general)
}

export function reflectionReply(input: string): { message: string; verse: Verse } {
  const topics = detectTopics(input)
  const primary = topics[0]
  const verse = verseForInput(input)

  const open = `Thank you for trusting this space with your heart. I read every word of "${echoBack(input)}", and I want you to know it matters.`

  let middle: string
  if (topics.includes('sadness') || topics.includes('stress') || topics.includes('anxiety')) {
    middle = `What you're feeling is real and it's allowed to be here. You don't have to carry it perfectly or explain it away. Naming it, like you just did, is already a quiet act of courage — and it loosens its grip. Be as gentle with yourself as you would be with someone you love. This heaviness is a season, not your whole story.`
  } else if (topics.includes('gratitude')) {
    middle = `There's something holy about a grateful heart — it notices the light that was there all along. Hold onto this feeling; let it teach you where your true treasures are. The more you name your blessings, the more clearly you'll see how held you truly are.`
  } else if (topics.includes('relationships')) {
    middle = `Relationships stretch us in the tenderest places. Whatever happened, you're allowed to feel it fully — and you're also free to choose grace, for them and for yourself. Forgiveness isn't saying it didn't hurt; it's refusing to let the hurt keep writing your story.`
  } else if (topics.includes('decision') || topics.includes('confusion')) {
    middle = `It's okay not to have the answer yet. Clarity often comes not all at once, but one honest step at a time. Trust that you don't have to see the whole staircase — only the next step. Wisdom is already reaching toward you.`
  } else {
    middle = `Whatever you're bringing here, it's safe in this quiet corner. There's no judgment, no rush, no need to be anything other than honest. Sometimes simply writing it down lets the heart breathe again.`
  }

  const close = `As you sit with this, let these words rest over you like a warm blanket. You are seen, you are loved, and you are not walking through this alone. 🌷`

  const message = [open, middle, close].join('\n\n')
  return { message, verse }
}

// ============================================================================
// DEVOTIONS 🌿
// ============================================================================
export interface Devotion {
  title: string
  verse: Verse
  body: string
  prayer: string
}

const DEVOTIONS: Devotion[] = [
  {
    title: 'Still Waters',
    verse: { ref: 'Psalm 23:2-3', text: 'He makes me lie down in green pastures, he leads me beside quiet waters, he refreshes my soul.' },
    body: 'Notice that the shepherd doesn\'t drive the sheep forward — he *makes them lie down*. Rest is not laziness; it is trust. In a world that measures you by output, choosing stillness is an act of faith that you are loved for who you are, not what you produce. Today, let yourself be led to quiet waters. Your soul is not a machine to optimize; it is a garden to tend.',
    prayer: 'Gentle Shepherd, quiet the noise inside me. Teach me that I am allowed to rest, and that I am loved even when I am still. Refresh my soul today. Amen.',
  },
  {
    title: 'New Every Morning',
    verse: { ref: 'Lamentations 3:22-23', text: 'His compassions never fail. They are new every morning; great is your faithfulness.' },
    body: 'Yesterday\'s mistakes do not get a vote in who you are today. Mercy arrives fresh with the sunrise, like dew on the grass — you cannot store it or run out of it. Whatever you carried into last night, you are allowed to set it down. This morning holds a clean page. You are not behind; you are simply beginning again, as everyone must, and that is grace.',
    prayer: 'Faithful God, thank You that Your mercy meets me new today. Help me release what is behind and step gently into this fresh morning. Amen.',
  },
  {
    title: 'The Small Seed',
    verse: { ref: 'Zechariah 4:10', text: 'Do not despise these small beginnings, for the Lord rejoices to see the work begin.' },
    body: 'You may feel that your progress is too small to matter — a single page read, one kind word, one honest prayer. But heaven rejoices over beginnings, not just finish lines. The mightiest oak was once a seed brave enough to break open in the dark. Don\'t measure today against the whole journey. Faithfulness in small things is exactly how great things are quietly built.',
    prayer: 'Lord, help me honour the small steps. Let me be faithful in little things and trust You with the growth I cannot yet see. Amen.',
  },
  {
    title: 'Be Still',
    verse: { ref: 'Psalm 46:10', text: 'Be still, and know that I am God.' },
    body: 'When the world spins fast, the invitation is not to run faster but to grow still. Stillness is where you remember who is actually holding everything together — and it isn\'t you. You can loosen your grip. The One who keeps the stars in their courses is entirely capable of keeping you. Breathe. You are not the one holding up the sky.',
    prayer: 'God, in the middle of my hurry, help me be still. Remind me that You are God and I am beloved. Let that be enough today. Amen.',
  },
  {
    title: 'Rejoice in This Day',
    verse: { ref: 'Psalm 118:24', text: 'This is the day the Lord has made; let us rejoice and be glad in it.' },
    body: 'Not tomorrow, not the day everything gets fixed — *this* day, exactly as it is, was made with intention. There is something good hidden in it waiting to be noticed: a warm drink, a patch of light, a breath freely given. Joy is not the absence of trouble; it is the practice of finding the gift inside the ordinary. Go looking today. It is there.',
    prayer: 'Maker of my days, open my eyes to the good gifts hidden in today. Teach my heart to rejoice, even here, even now. Amen.',
  },
]

export function randomDevotion(): Devotion {
  return pick(DEVOTIONS)
}

// ============================================================================
// GUIDED BIBLE STUDY 📖
// ============================================================================
export interface BibleStudy {
  theme: string
  passage: Verse
  observe: string
  reflect: string[]
  apply: string
}

export const BIBLE_STUDIES: BibleStudy[] = [
  {
    theme: 'Peace in Anxious Times',
    passage: { ref: 'Philippians 4:6-9', text: 'Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God. And the peace of God, which transcends all understanding, will guard your hearts and minds.' },
    observe: 'Paul writes this from prison, yet the theme is peace. Notice the pattern: prayer + petition + thanksgiving → peace that guards. Peace here is described as a guard — an active protector, not just a pleasant mood.',
    reflect: [
      'What are you most anxious about right now, honestly?',
      'What would it look like to bring that exact worry to God with thanksgiving instead of dread?',
      'Where in your life have you already seen a peace you couldn\'t explain?',
    ],
    apply: 'This week, each time worry rises, pause and turn it into a specific prayer that also names one thing you\'re thankful for. Watch how naming gratitude alongside the worry changes its weight.',
  },
  {
    theme: 'Rest for the Weary',
    passage: { ref: 'Matthew 11:28-30', text: 'Come to me, all you who are weary and burdened, and I will give you rest. Take my yoke upon you and learn from me, for I am gentle and humble in heart, and you will find rest for your souls.' },
    observe: 'A yoke joins two animals so the load is shared. Jesus doesn\'t remove the work — he offers to carry it *with* you. And he describes himself as gentle, not harsh. The rest offered is soul-deep, not just physical.',
    reflect: [
      'What burden are you carrying alone that you were never meant to carry alone?',
      'What does "gentle and humble" tell you about the character of the One inviting you?',
      'What would it feel like to hand over just one corner of your load today?',
    ],
    apply: 'Choose one burden and consciously "hand it over" in prayer each morning this week. Notice that rest often begins not when the work ends, but when you stop carrying it by yourself.',
  },
  {
    theme: 'Trusting the Path',
    passage: { ref: 'Proverbs 3:5-6', text: 'Trust in the Lord with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight.' },
    observe: 'The instruction isn\'t to *stop* thinking, but to stop leaning your full weight on your own limited view. Trust and submission come first; the straightened path follows. Clarity is a result of surrender, not a prerequisite for it.',
    reflect: [
      'Where are you leaning hard on your own understanding and feeling exhausted by it?',
      'What decision are you trying to force clarity on before you\'re willing to trust?',
      'What would "submitting your way" look like in one concrete area this week?',
    ],
    apply: 'Pick one uncertain area and pray, "I trust You with this," each time you\'re tempted to anxiously figure it all out. Let peace, not pressure, guide your next step.',
  },
]

export function randomBibleStudy(): BibleStudy {
  return pick(BIBLE_STUDIES)
}

// ============================================================================
// GRATITUDE PROMPTS, SCRIPTURE EXPLORER, PRAYER, JOURNAL PROMPTS
// ============================================================================
export const GRATITUDE_PROMPTS = [
  'What is one small comfort you often overlook?',
  'Who made your life a little softer this week?',
  'What is a challenge that secretly grew you?',
  'What did your body let you do today?',
  'What sound, smell, or sight brought you unexpected peace?',
  'What is something ordinary you would truly miss if it were gone?',
  'What door closed that you\'re now grateful never opened?',
  'What is one thing about *you* that you\'re thankful for?',
]

export function gratitudePrompt(): string {
  return pick(GRATITUDE_PROMPTS)
}

export const JOURNAL_PROMPTS = [
  'What is your heart quietly asking for today?',
  'Describe today using only gentle, honest words.',
  'What would you tell a dear friend who felt exactly how you feel now?',
  'What is one thing you\'re ready to release?',
  'Where did you notice beauty today, even briefly?',
  'What is a lesson this season is trying to teach you?',
  'If today had a color and a weather, what would they be, and why?',
  'What are you proud of that no one else noticed?',
]

export function journalPrompt(): string {
  return pick(JOURNAL_PROMPTS)
}

export const PRAYERS = [
  { title: 'A Prayer for Peace', text: 'Lord, quiet the storm inside me. Where there is worry, plant trust. Where there is noise, bring stillness. Help me rest in the truth that I am held. Amen.' },
  { title: 'A Prayer for Guidance', text: 'Father, I don\'t see the whole road, but I trust You see it all. Light the very next step and give me courage to take it. Amen.' },
  { title: 'A Prayer of Gratitude', text: 'Thank You for the gift of this day, for breath and light and small mercies. Open my eyes to the good all around me. Amen.' },
  { title: 'A Prayer for Strength', text: 'God, I feel weak, but You are strong. Carry what I cannot. Renew me like morning renews the sky, and help me begin again. Amen.' },
  { title: 'An Evening Prayer', text: 'As this day closes, I release what I could not finish and what I could not fix. Thank You for being with me through all of it. Grant me rest. Amen.' },
]

export function randomPrayer() {
  return pick(PRAYERS)
}

// A tiny scripture library the "Scripture Explorer" can browse by theme.
export const SCRIPTURE_LIBRARY: Record<string, Verse[]> = VERSES

// ============================================================================
// DAILY BRIEFING ☀️
// ============================================================================
export function dailyBriefing(name: string, balance: number): string[] {
  const now = new Date()
  const hour = now.getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  const encouragements = [
    'Today is a fresh page — you get to decide the first sentence.',
    'Small, gentle steps still carry you all the way home.',
    'You don\'t have to do everything today. Just the next kind thing.',
    'Progress, not perfection. Be soft with yourself.',
    'The calm you\'re looking for begins with a single slow breath.',
  ]
  const focuses = [
    'Choose one thing that would make today feel successful, and protect it.',
    'Drink some water, open a window, and let a little light in.',
    'Do the hardest small task first while your mind is fresh.',
    'Leave margin in your day — you are a person, not a schedule.',
    'End the day by naming one thing that went right.',
  ]
  return [
    `${greeting}, ${name}. ☀️ It's ${dateStr}.`,
    `Your account rests gently at **$${balance.toLocaleString()}** — steady and cared for.`,
    `A thought for today: ${pick(encouragements)}`,
    `Your gentle focus: ${pick(focuses)}`,
    `Take three slow breaths before you begin. The day will meet you at your pace. 🦌`,
  ]
}

// ============================================================================
// DECISION DESK 🎯
// ============================================================================
export function decisionAnalysis(input: string, optionA: string, optionB: string): string {
  const a = optionA.trim() || 'Option A'
  const b = optionB.trim() || 'Option B'
  const ctx = input.trim() ? `You're weighing: "${echoBack(input)}".` : `You're weighing two paths.`
  return [
    `🎯 ${ctx} Let's think it through calmly — no pressure, just clarity.`,
    `**Step 1 — Name the real stakes.** Ask what you're *actually* choosing between. Often it's not "${a}" vs "${b}", but two versions of your future self. Which version do you want to become?`,
    `**Step 2 — Three honest questions for each option.** For both "${a}" and "${b}", ask: (1) Which will I respect myself for in a year? (2) Which am I drawn to for growth, and which for fear or comfort? (3) What's the realistic worst case, and could I handle it?`,
    `**Step 3 — Listen to your body.** Imagine you've fully chosen "${a}". Sit with it for a moment — relief or dread? Now do the same with "${b}". Your body often knows before your mind admits it.`,
    `**Step 4 — Reduce the risk.** Is there a small, reversible experiment that lets you test a direction without betting everything? Most decisions feel lighter when you make them smaller and reversible.`,
    `My gentle nudge: if one option scares you *because it matters* and the other only feels safe, growth usually lives in the first. But sleep on it — if you wake up relieved by your choice, trust that. You've got this. 🌷`,
  ].join('\n\n')
}

// ============================================================================
// PERSONAL GROWTH 🌱 / WISDOM 📜 / QUESTION CORNER 💡
// ============================================================================
export const WISDOM_QUOTES = [
  { text: 'You do not rise to the level of your goals; you fall to the level of your systems.', by: 'On habits' },
  { text: 'The two most important days in your life are the day you are born and the day you find out why.', by: 'On purpose' },
  { text: 'What you are is a gift; what you become is your gift back.', by: 'On growth' },
  { text: 'Between stimulus and response there is a space. In that space is our power to choose.', by: 'On freedom' },
  { text: 'Comparison is the thief of joy.', by: 'On contentment' },
  { text: 'The wound is the place where the light enters you.', by: 'On healing' },
  { text: 'Be patient with all that is unsolved in your heart, and try to love the questions themselves.', by: 'On patience' },
  { text: 'A ship is safe in harbor, but that is not what ships are for.', by: 'On courage' },
  { text: 'How we spend our days is, of course, how we spend our lives.', by: 'On time' },
  { text: 'You can\'t stop the waves, but you can learn to surf.', by: 'On acceptance' },
]

export function randomWisdom() {
  return pick(WISDOM_QUOTES)
}

export function questionAnswer(input: string): string {
  // Reuse Professor Ted's depth but framed as a curious answer.
  const topics = detectTopics(input)
  const opener = `💡 Great question — "${echoBack(input)}". Here's my honest, thoughtful take.`
  const body = guidanceFor(topics[0], input)
  const followUp = `The most useful answers usually come from a slightly sharper question. If you tell me the specific part that matters most to you, I can go deeper and make it genuinely useful.`
  return [opener, body, followUp].join('\n\n')
}

export const GROWTH_CHALLENGES = [
  'Today, do one small thing you\'ve been avoiding — before you feel ready.',
  'Give someone a specific, honest compliment.',
  'Sit in silence for five minutes with no phone.',
  'Write down three things you did well this week.',
  'Say a kind, true sentence to yourself in the mirror.',
  'Do a task 1% better than yesterday — that\'s all.',
  'Ask for help with one thing you\'ve been struggling with alone.',
  'Rest without guilt for twenty minutes.',
]

export function growthChallenge() {
  return pick(GROWTH_CHALLENGES)
}
